import React, { useEffect, useMemo, useState } from "react";
import {
  FaArrowLeft,
  FaArrowRight,
  FaCalendarAlt,
  FaNewspaper,
  FaTimes,
  FaUserEdit,
} from "react-icons/fa";

import Reveal from "../../../components/ui/Reveal";
import SectionTitle from "../HomePage/SectionTitle";

import { getNews } from "./../../../services/newsServices";
import {
  formatDateFR,
  resolveMediaUrl,
  truncateText,
} from "../HomePage/homeUtils";

const NEWS_PER_PAGE = 3;
const FALLBACK_NEWS_IMAGE = "/img/placeholder-news.jpg";

const stripHtml = (value = "") =>
  String(value)
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const hasHtml = (value = "") => /<\/?[a-z][\s\S]*>/i.test(String(value));

const LatestNews = ({ sectionId }) => {
  const [newsList, setNewsList] = useState([]);
  const [selectedNews, setSelectedNews] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const fetchNews = async () => {
      try {
        setLoading(true);

        const latestNews = await getNews();

        const activeNews = Array.isArray(latestNews)
          ? latestNews
              .filter(
                (news) =>
                  news.is_active &&
                  String(news.section_id) === String(sectionId)
              )
              .sort((a, b) => {
                const dateA = new Date(
                  a.created_at || a.updated_at || 0
                ).getTime();
                const dateB = new Date(
                  b.created_at || b.updated_at || 0
                ).getTime();

                return dateB - dateA;
              })
          : [];

        if (mounted) {
          setNewsList(activeNews);
          setCurrentPage(1);
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des actualités:", error);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    fetchNews();

    return () => {
      mounted = false;
    };
  }, [sectionId]);

  const totalPages = Math.ceil(newsList.length / NEWS_PER_PAGE);

  const currentNews = useMemo(() => {
    const start = (currentPage - 1) * NEWS_PER_PAGE;
    return newsList.slice(start, start + NEWS_PER_PAGE);
  }, [newsList, currentPage]);

  if (loading) return null;
  if (newsList.length === 0) return null;

  return (
    <section className="bg-white py-16 dark:bg-slate-900 sm:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <SectionTitle
          eyebrow="Actualités"
          title="Les dernières "
          highlight="actualités"
          subtitle="Retrouvez les informations récentes publiées par cette association."
        />

        <div className="grid gap-6 md:grid-cols-3">
          {currentNews.map((news, index) => {
            const imageUrl = resolveMediaUrl(
              news.image_url,
              FALLBACK_NEWS_IMAGE
            );

            return (
              <Reveal key={news.id} delay={index * 0.04}>
                <article className="group flex h-full flex-col overflow-hidden rounded-[1.75rem] border border-slate-200 bg-slate-50 shadow-sm transition hover:-translate-y-1 hover:shadow-xl dark:border-white/10 dark:bg-slate-950">
                  <button
                    type="button"
                    onClick={() => setSelectedNews(news)}
                    className="relative block h-56 w-full overflow-hidden text-left"
                  >
                    <img
                      src={imageUrl}
                      alt={news.title}
                      className="h-full w-full object-cover transition duration-500 group-hover:scale-110"
                      onError={(event) => {
                        event.currentTarget.src = FALLBACK_NEWS_IMAGE;
                      }}
                    />

                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/65 via-slate-950/10 to-transparent" />

                    <div className="absolute left-4 top-4 flex h-11 w-11 items-center justify-center rounded-2xl bg-white text-primary shadow">
                      <FaNewspaper />
                    </div>
                  </button>

                  <div className="flex flex-1 flex-col p-5">
                    <div className="mb-4 flex flex-wrap gap-2 text-xs font-bold">
                      {news.author && (
                        <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-primary">
                          <FaUserEdit />
                          {news.author.prenom} {news.author.nom}
                        </span>
                      )}

                      {(news.created_at || news.updated_at) && (
                        <span className="inline-flex items-center gap-2 rounded-full bg-slate-200 px-3 py-1 text-slate-600 dark:bg-white/10 dark:text-slate-300">
                          <FaCalendarAlt />
                          {formatDateFR(news.created_at || news.updated_at)}
                        </span>
                      )}
                    </div>

                    <h3 className="text-xl font-bold text-slate-950 dark:text-white">
                      {news.title}
                    </h3>

                    {news.content && (
                      <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-300">
                        {truncateText(stripHtml(news.content), 180)}
                      </p>
                    )}

                    <div className="mt-auto pt-5">
                      <button
                        type="button"
                        onClick={() => setSelectedNews(news)}
                        className="inline-flex items-center gap-2 rounded-2xl bg-primary px-4 py-2 text-sm font-bold text-white transition hover:opacity-90"
                      >
                        Lire l’actualité
                        <FaArrowRight />
                      </button>
                    </div>
                  </div>
                </article>
              </Reveal>
            );
          })}
        </div>

        {newsList.length > NEWS_PER_PAGE && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPrev={() => setCurrentPage((p) => Math.max(1, p - 1))}
            onNext={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          />
        )}
      </div>

      {selectedNews && (
        <NewsModal news={selectedNews} onClose={() => setSelectedNews(null)} />
      )}
    </section>
  );
};

function NewsModal({ news, onClose }) {
  const imageUrl = resolveMediaUrl(news.image_url, FALLBACK_NEWS_IMAGE);
  const dateValue = news.created_at || news.updated_at;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/85 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <article
        className="relative max-h-[92vh] w-full max-w-5xl overflow-hidden rounded-[2rem] bg-white shadow-2xl dark:bg-slate-950"
        onClick={(event) => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 z-30 flex h-11 w-11 items-center justify-center rounded-full bg-white text-slate-950 shadow-lg transition hover:scale-105 dark:bg-slate-900 dark:text-white"
          aria-label="Fermer"
        >
          <FaTimes />
        </button>

        <div className="max-h-[92vh] overflow-y-auto">
          <div className="relative flex h-[58vh] min-h-[320px] max-h-[560px] items-center justify-center overflow-hidden bg-slate-950">
            <img
              src={imageUrl}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 h-full w-full scale-110 object-cover opacity-35 blur-2xl"
              onError={(event) => {
                event.currentTarget.src = FALLBACK_NEWS_IMAGE;
              }}
            />

            <div className="absolute inset-0 bg-slate-950/35" />

            <img
              src={imageUrl}
              alt={news.title || "Actualité"}
              className="relative z-10 h-full w-full object-contain p-4 sm:p-6"
              onError={(event) => {
                event.currentTarget.src = FALLBACK_NEWS_IMAGE;
              }}
            />
          </div>

          <div className="p-6 sm:p-8 lg:p-10">
            <div className="mb-5 flex flex-wrap gap-2 text-xs font-bold">
              <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3 py-1 text-primary">
                <FaNewspaper />
                Actualité
              </span>

              {news.author && (
                <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-slate-600 dark:bg-white/10 dark:text-slate-300">
                  <FaUserEdit />
                  {news.author.prenom} {news.author.nom}
                </span>
              )}

              {dateValue && (
                <span className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-slate-600 dark:bg-white/10 dark:text-slate-300">
                  <FaCalendarAlt />
                  {formatDateFR(dateValue)}
                </span>
              )}
            </div>

            <h2 className="max-w-4xl text-3xl font-bold leading-tight text-slate-950 dark:text-white sm:text-4xl">
              {news.title}
            </h2>

            <div className="mt-6 rounded-[1.5rem] border border-slate-200 bg-slate-50 p-5 dark:border-white/10 dark:bg-slate-900">
              {news.content ? (
                hasHtml(news.content) ? (
                  <div
                    className="prose prose-slate max-w-none leading-8 dark:prose-invert prose-a:text-primary prose-strong:text-slate-950 dark:prose-strong:text-white"
                    dangerouslySetInnerHTML={{ __html: news.content }}
                  />
                ) : (
                  <p className="whitespace-pre-line text-base leading-8 text-slate-700 dark:text-slate-300">
                    {news.content}
                  </p>
                )
              ) : (
                <p className="text-slate-500 dark:text-slate-400">
                  Aucun contenu détaillé n’est disponible pour cette actualité.
                </p>
              )}
            </div>

            <div className="mt-8 flex justify-end">
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-white/10 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-white/10"
              >
                <FaTimes />
                Fermer
              </button>
            </div>
          </div>
        </div>
      </article>
    </div>
  );
}

function Pagination({ currentPage, totalPages, onPrev, onNext }) {
  return (
    <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
      <button
        type="button"
        onClick={onPrev}
        disabled={currentPage === 1}
        className="inline-flex items-center gap-2 rounded-2xl bg-slate-900 px-4 py-2 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-40 dark:bg-white dark:text-slate-950"
      >
        <FaArrowLeft />
        Précédent
      </button>

      <span className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-600 dark:border-white/10 dark:bg-slate-950 dark:text-slate-300">
        Page {currentPage} / {totalPages}
      </span>

      <button
        type="button"
        onClick={onNext}
        disabled={currentPage === totalPages}
        className="inline-flex items-center gap-2 rounded-2xl bg-primary px-4 py-2 text-sm font-bold text-white disabled:cursor-not-allowed disabled:opacity-40"
      >
        Suivant
        <FaArrowRight />
      </button>
    </div>
  );
}

export default LatestNews;